import { useEffect, useRef } from 'react'
import { reportSearchComparison } from '../searchReporter'

const emptyIndexResult = {
  hits: [], hitCount: 0, hasMore: false, loadMore: () => {},
}

const emptyResults = {
  suggestions: [],
  hits: [],
  indexResults: {
    repos: { ...emptyIndexResult },
    folders: { ...emptyIndexResult },
    // eslint-disable-next-line camelcase
    sets_questions: { ...emptyIndexResult },
  },
}

/**
 * Headless shadow/challenger provider stub.
 *
 * Implements the same interface as AlgoliaSearchProvider so the orchestrator
 * can render it as a sibling without knowing its implementation.
 *
 * Currently returns empty results immediately and logs the comparison via
 * searchReporter. Replace the body of the useEffect to connect a real engine.
 *
 * Props:
 *   query            {string}   Controlled query from SearchOrchestrator.
 *   options          {Object}   Same shape as AlgoliaSearchProvider options.
 *   onResults        {Function} Callback — called with the results shape.
 *   displayedResults {Object}   The display provider's current results for
 *                               comparison logging. Provided by orchestrator.
 *   displayedProvider {string}  ID of the current display provider.
 */
function ShadowProvider({
  query,
  options, // eslint-disable-line no-unused-vars
  onResults,
  displayedResults,
  displayedProvider,
}) {
  // Hold callbacks and live data in refs so the effect's deps array can stay
  // narrow (just `query`). Including onResults/displayedResults in deps would
  // cause the effect to re-fire every parent render and the comparison report
  // would loop indefinitely with the display provider's updates.
  const onResultsRef = useRef(onResults)
  onResultsRef.current = onResults
  const displayedResultsRef = useRef(displayedResults)
  displayedResultsRef.current = displayedResults
  const displayedProviderRef = useRef(displayedProvider)
  displayedProviderRef.current = displayedProvider

  useEffect(() => {
    if (!query) return

    // Stub: resolve immediately with empty results.
    // Replace this with a real search call when the challenger engine is ready.
    const shadowResults = { ...emptyResults }

    onResultsRef.current(shadowResults)

    reportSearchComparison({
      query,
      displayedProvider: displayedProviderRef.current,
      displayedResults: displayedResultsRef.current,
      shadowProvider: 'shadow',
      shadowResults,
    })
  }, [query])

  return null
}

export default ShadowProvider
